import {createContext, useContext, useState, useCallback, useMemo, useEffect} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {getToken, setToken as persistToken, clearToken, AUTH_EVENT_KEY} from '@/utils/authToken';

const AuthContext = createContext(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({children}) => {
    const [token, setToken] = useState(() => getToken());
    const queryClient = useQueryClient();

    const login = useCallback((authToken) => {
        persistToken(authToken);
        setToken(authToken);
        // Refetch per-user data (the /config `square` block + the /account
        // `is_test_account` that selects sandbox vs production) with the new
        // token. Logout already wipes the cache via queryClient.clear().
        queryClient.invalidateQueries({queryKey: ['app-config']});
        queryClient.invalidateQueries({queryKey: ['user', 'account']});
    }, [queryClient]);

    const logout = useCallback(() => {
        clearToken();
        queryClient.clear();
        setToken(null);
    }, [queryClient]);

    const isAuthenticated = !!token;

    // Keep auth state in sync with the cookie so ProtectedRoute can react. When the
    // token disappears — a 401 cleared it (this tab), the user logged out in another
    // tab, or it expired — flip to signed-out and let ProtectedRoute send the user to
    // /signin (it saves the return path), instead of leaving the page piling up 401s.
    // Re-checked on the 401 event, the cross-tab storage signal, and on tab focus.
    useEffect(() => {
        const syncFromCookie = () => {
            const current = getToken() || null;
            setToken((prev) => (prev === current ? prev : current));
            if (!current) queryClient.clear();
        };
        const onStorage = (e) => { if (e.key === AUTH_EVENT_KEY || e.key === null) syncFromCookie(); };
        const onVisible = () => { if (document.visibilityState === 'visible') syncFromCookie(); };
        window.addEventListener('hopn:unauthorized', syncFromCookie);
        window.addEventListener('storage', onStorage);
        window.addEventListener('focus', syncFromCookie);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.removeEventListener('hopn:unauthorized', syncFromCookie);
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('focus', syncFromCookie);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [queryClient]);

    const value = useMemo(() => ({token, isAuthenticated, login, logout}), [token, isAuthenticated, login, logout]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
